import {
  TRACEABILITY_SET_ORDERS,
  TRACEABILITY_SET_ORDERS_LOADING_STATUS,
  TRACEABILITY_SET_SEARCH_PARAMS,
  TRACEABILITY_SET_EXPAND_DETAILS,
  TRACEABILITY_SET_COORDINATES_LOADING_STATUS,
  TRACEABILITY_SET_BATCH_TRANSPORT_COORDINATES,
  TRACEABILITY_SET_BATCH_PLACE_EXECUTION_COORDINATES,
  TRACEABILITY_CLEAR_ALL_COORDINATES,
  TRACEABILITY_SET_TOTAL_EXAMS,
  TRACEABILITY_SET_CURRENT_TAB,
  TRACEABILITY_SET_GROUPED_EXAMS_COUNT,
  TRACEABILITY_SET_COUNTERS_LOADING_STATUS,
  TRACEABILITY_SET_PAGINATION,
  TRACEABILITY_RESET_PAGINATION,
  TRACEABILITY_APPEND_ORDERS,
  TRACEABILITY_CLEAR_ORDERS,
} from '../types'
import traceabilitySearchService from '../../api/traceabilitySearch'
import notify from '@/api/notification'
import analytics from '../../api/analytics'
import moment from 'moment'
import {
  TRACEABILITY_SEARCH_DATE,
  TRACEABILITY_SEARCH_PERIOD_DAYS_DEFAULT,
} from '../../enums'

const FILTER_TYPE_CONVERSION_MAP = {
  PN: 'nomePaciente', // TRACEABILITY_SEARCH_PATIENT_NAME
  OA: 'osAlvaro', // TRACEABILITY_SEARCH_OS_ALVARO
  OL: 'osLis', // TRACEABILITY_SEARCH_OS_LIS
  AM: 'numeroAmostra', // TRACEABILITY_SEARCH_SAMPLE
  BA: 'numeroLote', // TRACEABILITY_SEARCH_BATCH
  NTO: 'nto', // TRACEABILITY_SEARCH_NTO
  DT: 'periodo', // TRACEABILITY_SEARCH_DATE
}

const getDefaultDates = () => {
  const endDate = moment().format('DD/MM/YYYY')
  const startDate = moment()
    .subtract(TRACEABILITY_SEARCH_PERIOD_DAYS_DEFAULT, 'days')
    .format('DD/MM/YYYY')
  return { startDate, endDate }
}

const defaultDates = getDefaultDates()

// ========================================
// FUNÇÃO UTILITÁRIA PARA PARÂMETROS
// ========================================

const buildApiParams = (state, additionalParams = {}) => {
  let dateParsed = null

  if (state.filterType === 'periodo') {
    const [startDate, endDate] = state?.filterValue?.split('/')
    const endDateAddedDay = moment(endDate).add(1, 'days').format('YYYY-MM-DD')
    dateParsed = `${startDate}/${endDateAddedDay}`
  }

  const filterValue = dateParsed ?? state.filterValue
  const hasFilterValue = filterValue && filterValue.trim() !== ''

  return Object.assign(
    {
      filterType: hasFilterValue ? state.filterType : 'status',
      filterValue: hasFilterValue ? filterValue : '',
      status: state?.status ?? '',
      limit: state.pageSize || 20,
      nextCursor: state.nextCursor || '',
    },
    additionalParams
  )
}

const state = {
  currentTab: 0,
  loadingOrderStatus: 'STATUS_INITIAL',
  loadingCountersStatus: 'STATUS_INITIAL',

  orders: [],
  groupedExamsCount: {},
  totalExams: 0,

  nextCursor: '',
  pageSize: 20,
  hasNext: false,

  typeOfSearch: TRACEABILITY_SEARCH_DATE,
  query: '',
  startDate: defaultDates.startDate,
  endDate: defaultDates.endDate,
  status: null,
  filterType: '',
  filterValue: '',

  loadingCoordinatesStatus: 'STATUS_INITIAL',
  transportCoordinates: {},
  placeExecutionCoordinates: {},
}

const mutations = {
  [TRACEABILITY_SET_ORDERS](state, orders) {
    state.orders = orders
  },
  [TRACEABILITY_SET_ORDERS_LOADING_STATUS](state, status) {
    state.loadingOrderStatus = status
  },
  [TRACEABILITY_SET_COUNTERS_LOADING_STATUS](state, status) {
    state.loadingCountersStatus = status
  },
  [TRACEABILITY_SET_SEARCH_PARAMS](state, params) {
    state.typeOfSearch = params.typeOfSearch
    state.query = params.q
    state.status = params.status
    state.startDate = params.startDate
    state.endDate = params.endDate

    if (params.typeOfSearch === 'NTO') {
      state.placeExecution = params.nto || params.q
    } else {
      state.placeExecution = params.nto
    }

    state.filterType = FILTER_TYPE_CONVERSION_MAP[params.typeOfSearch] || 'nomePaciente'

    if (params.typeOfSearch === 'DT') {
      const startDate = params.startDate
        ? moment(params.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : null
      const endDate = params.endDate
        ? moment(params.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : null
      state.filterValue = `${startDate}/${endDate}`
    } else if (params.typeOfSearch === 'NTO') {
      state.filterValue = params.nto || params.q || ''
    } else {
      state.filterValue = params.q || ''
    }
  },
  [TRACEABILITY_SET_EXPAND_DETAILS](state, index) {
    state.orders[index].expanded = !state.orders[index].expanded
  },
  [TRACEABILITY_SET_COORDINATES_LOADING_STATUS](state, status) {
    state.loadingCoordinatesStatus = status
  },
  [TRACEABILITY_SET_BATCH_TRANSPORT_COORDINATES](state, coordinates) {
    state.transportCoordinates = coordinates
  },
  [TRACEABILITY_SET_BATCH_PLACE_EXECUTION_COORDINATES](state, coordinates) {
    state.placeExecutionCoordinates = coordinates
  },
  [TRACEABILITY_CLEAR_ALL_COORDINATES](state) {
    state.transportCoordinates = {}
    state.placeExecutionCoordinates = {}
  },
  [TRACEABILITY_SET_TOTAL_EXAMS](state, total) {
    state.totalExams = total
  },
  [TRACEABILITY_SET_CURRENT_TAB](state, currentTab) {
    state.currentTab = currentTab
  },
  [TRACEABILITY_SET_GROUPED_EXAMS_COUNT](state, groupedExamsCount) {
    state.groupedExamsCount = groupedExamsCount
  },
  [TRACEABILITY_SET_PAGINATION](state, { nextCursor, hasNext }) {
    state.nextCursor = nextCursor || ''
    state.hasNext = hasNext || false
  },
  [TRACEABILITY_RESET_PAGINATION](state) {
    state.nextCursor = ''
    state.hasNext = false
  },
  [TRACEABILITY_APPEND_ORDERS](state, orders) {
    state.orders = state.orders.concat(orders)
  },
  [TRACEABILITY_CLEAR_ORDERS](state) {
    state.orders = []
  },
}

const getters = {
  traceabilityOrders: state => {
    return state.orders
  },
  traceabilityGroupedExamsCount: state => state.groupedExamsCount,
  traceabilityHasNext: state => state.hasNext,
  traceabilityNextCursor: state => state.nextCursor,
  traceabilityTotalCount: state => {
    const counts = state.groupedExamsCount
    if (!counts || typeof counts !== 'object') return 0

    const filteredCounts = Object.keys(counts)
      .filter(
        key =>
          key !== 'AMOSTRA_OBTIDA' &&
          key !== 'CANCELADO_CLIENTE' &&
          key !== 'PENDENCIA_CRIADA' &&
          key !== 'PENDENCIA_RESPONDIDA' &&
          key !== 'EM_TRANSITO_ENTRE_NTO' &&
          key !== 'RECOLETADO' &&
          key !== 'SOLICITADA_RECOLETA' &&
          key !== 'ATRASADO' &&
          key !== 'EM_LIBERACAO' &&
          key !== 'ADIANTADO'
      )
      .reduce((acc, key) => {
        acc[key] = counts[key]
        return acc
      }, {})

    return Object.values(filteredCounts).reduce((total, count) => {
      return total + (typeof count === 'number' ? count : 0)
    }, 0)
  },
  traceabilityStatus: state => {
    return state.status
  },
  traceabilityTotalExams: state => {
    return state.totalExams
  },
  traceabilityOrdersLoadingStatus: state => {
    return state.loadingOrderStatus
  },
  traceabilityCountersLoadingStatus: state => {
    return state.loadingCountersStatus
  },
  transportLocalization: state => {
    return state.transportCoordinates
  },
  placeExecutionLocalization: state => {
    return state.placeExecutionCoordinates
  },
  loadingCoordinatesStatus: state => {
    return state.loadingCoordinatesStatus
  },
  traceabilityFilterState: state => ({
    typeOfSearch: state.typeOfSearch,
    query: state.query,
    status: state.status,
    startDate: state.startDate,
    endDate: state.endDate,
    placeExecution: state.placeExecution,
    filterType: state.filterType,
    filterValue: state.filterValue,
  }),
  traceabilityCurrentTab: state => state.currentTab,
}

const actions = {
  convertLegacyFilterType(legacyType) {
    return FILTER_TYPE_CONVERSION_MAP[legacyType] || 'nomePaciente'
  },

  // ========================================
  // MÉTODOS DE API
  // ========================================

  getTraceabilityGroupedExams: async function (
    { commit, state, dispatch },
    { clearDataFirst = false } = {}
  ) {
    try {
      commit(TRACEABILITY_SET_ORDERS_LOADING_STATUS, 'STATUS_LOADING')

      if (clearDataFirst) {
        commit(TRACEABILITY_CLEAR_ORDERS)
        commit(TRACEABILITY_RESET_PAGINATION)
      }

      const params = buildApiParams(state)

      const response = await traceabilitySearchService.getGroupedExams(params)

      if (response?.data?.data?.length > 0) {
        const orders = response.data.data.map(order =>
          Object.assign({}, order, { expanded: false })
        )

        commit(TRACEABILITY_SET_ORDERS, orders)
        commit(TRACEABILITY_SET_PAGINATION, {
          nextCursor: response.data.nextCursor || '',
          hasNext: response.data.hasNext || false,
        })

        submitAnalyticsEvent('Qtdade de OS exibidas - ' + orders.length)
      } else {
        commit(TRACEABILITY_SET_ORDERS, [])
        commit(TRACEABILITY_SET_PAGINATION, {
          nextCursor: '',
          hasNext: false,
        })
      }
    } catch (error) {
      commit(TRACEABILITY_SET_ORDERS, [])
      commit(TRACEABILITY_SET_PAGINATION, {
        nextCursor: '',
        hasNext: false,
      })
      if (error.response?.status !== 404) {
        notify({
          title: 'AVISO:',
          message: 'Erro ao carregar informações. Por favor, tente novamente.',
          action: {
            title: 'RECARREGAR',
            handler: reload,
          },
        })
      }
    } finally {
      commit(TRACEABILITY_SET_ORDERS_LOADING_STATUS, 'STATUS_FINISHED')
    }
  },

  getTraceabilityGetGroupedExamsCount: async function ({ commit, state }, { onlyBatched = false } = {}) {
    try {
      commit(TRACEABILITY_SET_COUNTERS_LOADING_STATUS, 'STATUS_LOADING')
      commit(TRACEABILITY_SET_GROUPED_EXAMS_COUNT, {})

      const params = buildApiParams(state, onlyBatched ? { onlyBatched: true } : {})

      const response = await traceabilitySearchService.getGroupedExamsCount(params)
      commit(TRACEABILITY_SET_GROUPED_EXAMS_COUNT, response.data)
    } catch (error) {
      commit(TRACEABILITY_SET_GROUPED_EXAMS_COUNT, {})
    } finally {
      commit(TRACEABILITY_SET_COUNTERS_LOADING_STATUS, 'STATUS_FINISHED')
    }
  },
  clearAllTraceabilityData: function ({ commit }) {
    commit(TRACEABILITY_CLEAR_ORDERS)
    commit(TRACEABILITY_SET_TOTAL_EXAMS, 0)
  },
  selectTraceabilityParams: function ({ commit }, paramsList) {
    commit(TRACEABILITY_SET_ORDERS_LOADING_STATUS, 'STATUS_INITIAL')
    commit(TRACEABILITY_SET_SEARCH_PARAMS, paramsList)
  },
  expandTraceabilityDetails({ commit }, index) {
    commit(TRACEABILITY_SET_EXPAND_DETAILS, index)
  },
  getCurrentBatchCoordinates({ commit }, batchBarcode) {
    commit(TRACEABILITY_SET_COORDINATES_LOADING_STATUS, 'STATUS_LOADING')

    traceabilitySearchService
      .getCurrentBatchCoordinates(batchBarcode)
      .then(response => {
        let coordinates = response.data
        commit(TRACEABILITY_SET_BATCH_TRANSPORT_COORDINATES, coordinates)
        commit(TRACEABILITY_SET_COORDINATES_LOADING_STATUS, 'STATUS_FINISHED')
      })
      .catch(() => {
        commit(TRACEABILITY_SET_COORDINATES_LOADING_STATUS, 'STATUS_FINISHED')
      })
  },
  getPlaceExecutionCoordinates({ commit }, placeExecutionId) {
    commit(TRACEABILITY_SET_COORDINATES_LOADING_STATUS, 'STATUS_LOADING')

    traceabilitySearchService
      .getPlaceExecutionCoordinates(placeExecutionId)
      .then(response => {
        let coordinates = response.data
        commit(TRACEABILITY_SET_BATCH_PLACE_EXECUTION_COORDINATES, coordinates)
        commit(TRACEABILITY_SET_COORDINATES_LOADING_STATUS, 'STATUS_FINISHED')
      })
      .catch(() => {
        commit(TRACEABILITY_SET_COORDINATES_LOADING_STATUS, 'STATUS_FINISHED')
      })
  },
  clearAllCoordinates({ commit }) {
    commit(TRACEABILITY_CLEAR_ALL_COORDINATES)
  },
  setCurrentTabTraceability: function ({ commit }, currentTab) {
    commit(TRACEABILITY_SET_CURRENT_TAB, currentTab)
  },

  resetTraceabilityStore: function ({ commit }) {
    const initialParams = {
      typeOfSearch: 'PN',
      q: '',
      status: null,
      startDate: null,
      endDate: null,
      nto: null,
    }
    commit(TRACEABILITY_SET_SEARCH_PARAMS, initialParams)

    commit(TRACEABILITY_CLEAR_ORDERS)
    commit(TRACEABILITY_SET_TOTAL_EXAMS, 0)
    commit(TRACEABILITY_SET_GROUPED_EXAMS_COUNT, {})

    commit(TRACEABILITY_SET_ORDERS_LOADING_STATUS, 'STATUS_INITIAL')

    commit(TRACEABILITY_SET_CURRENT_TAB, 0)

    commit(TRACEABILITY_CLEAR_ALL_COORDINATES)
  },

  loadNextTraceabilityPage: async function ({ commit, state }) {
    try {
      if (!state.hasNext || state.loadingOrderStatus === 'STATUS_LOADING') {
        return false
      }

      commit(TRACEABILITY_SET_ORDERS_LOADING_STATUS, 'STATUS_LOADING')

      const params = buildApiParams(state, { nextCursor: state.nextCursor })

      const response = await traceabilitySearchService.getGroupedExams(params)

      if (response?.data?.data?.length > 0) {
        const orders = response.data.data.map(order =>
          Object.assign({}, order, { expanded: false })
        )

        commit(TRACEABILITY_APPEND_ORDERS, orders)
        commit(TRACEABILITY_SET_PAGINATION, {
          nextCursor: response.data.nextCursor || '',
          hasNext: response.data.hasNext || false,
        })

        return true
      } else {
        commit(TRACEABILITY_SET_PAGINATION, {
          nextCursor: state.nextCursor,
          hasNext: false,
        })
        return false
      }
    } catch (error) {
      console.error('Erro ao carregar próxima página:', error)
      return false
    } finally {
      commit(TRACEABILITY_SET_ORDERS_LOADING_STATUS, 'STATUS_FINISHED')
    }
  },

  resetTraceabilityPagination: function ({ commit }) {
    commit(TRACEABILITY_RESET_PAGINATION)
    commit(TRACEABILITY_CLEAR_ORDERS)
  },
}

let reload = function () {
  window.location.reload()
}

let submitAnalyticsEvent = action => {
  analytics.pushAnalyticsEvent('ga-event', {
    category: 'Rastreabilidade',
    action: action,
  })
}

export default {
  state,
  mutations,
  actions,
  getters,
}
export { mutations, actions, getters }
